
import { defineComponent, nextTick, reactive, ref, toRefs, watch, computed, createVNode, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import store from '@/store'
import { zh_tran, objTran } from '@/utils/lang.js'
import {
  PlusOutlined,
  BoldOutlined,
  CalendarOutlined,
  PictureOutlined,
  DoubleLeftOutlined,
  LeftOutlined,
  RightOutlined,
  DoubleRightOutlined,
  SmileOutlined
} from '@ant-design/icons-vue'
import { Modal, message } from 'ant-design-vue'
import optionalshares from '@/components/optionalshares.vue'
import globalsets from '@/utils/globalsets'
import allApi from '@/api/allApi'
import Divedit from '@/views/stocknotes/divedit.vue'
import stocknotesApi from '@/api/stocknotesApi'
import stock from '@/utils/stock'
import Nodata from '@/components/nodata.vue'
import type { stockType, note, dateOption, pageDataType } from './handle'
import emojis from './emojis'
import { emojiArray, emojiArrayV2 } from './transform'

const project = require('@/views/stocknotes/project.json')

export default defineComponent({
  components: {
    PlusOutlined,
    BoldOutlined,
    CalendarOutlined,
    PictureOutlined,
    optionalshares,
    Divedit,
    Nodata,
    DoubleLeftOutlined,
    LeftOutlined,
    RightOutlined,
    DoubleRightOutlined,
    SmileOutlined
  },
  setup(props: any) {
    const route = useRoute()
    const diveditRef = ref()
    const today = Number((Date.now() / 1000 + '').split('.')[0])

    // 默认参数，路由没有时使用
    const defaultUid = (route.query.uid as string)
    const defaultProjectID = (route.query.ProjectID as string)
    const defaultProjectName = (route.query.ProjectName as string)

    // 判断是否有沪深股票权限：hashs=1有权限，hashs=0或不传无权限
    // 无权限时需要限制灰色和禁止跳转
    const isFangDeZhiTou = computed(() => route.query.hashs != '1')

    // 语言切换：根据路由参数 Accept-Language 判断，zh-Hant 为繁体，默认 zh-Hans 为简体
    const lang = (route.query['Accept-Language'] as string) || 'zh-Hans'
    const isTraditional = lang === 'zh-Hant'

    // 保存光标位置的 Range 对象（不能放在 reactive 中）
    let savedRange: Range | null = null

    const pageData = reactive({
      radiovalue: 4,
      searchText: '',
      text: '',
      getCalendarContainer: (e: HTMLElement) => e.parentNode,
      token: '',
      noteslist: [] as any[],
      currentProject: {} as any,
      content: '',
      options: [] as any[],
      showSelect: false,
      showDateSelect: false,
      showEmojiSelect: false,
      activeEmojiTab: 'basic',
      currentNote: {
        index: -1,
        Title: '',
        Content: '',
        ID: '',
        PubTime: 0
      } as any,
      selectionIndex: 0,
      anchorNode: {} as any,
      selection: {} as any,
      theme: (route.query.theme as string) || 'light',
      emptynotetitle: isTraditional ? '<新建個股筆記>' : '<新建个股笔记>',
      switchtype: route.query.switchtype as string,
      tradeDays: [] as string[],
      selectedDate: window.moment() as any,
      loading: false,
      pageLoaded: false,
      isFirstLoad: true,
      dateChanged: false,
      draftNoteTab1: null as any, // 全部笔记Tab的草稿
      draftNoteTab4: null as any, // 个股相关Tab的草稿
      lastValidDate: window.moment() as any, // 记录上一次有效日期
      originalTitle: '',
      originalContent: '',
      originalDate: ''
    })

    pageData.currentProject = project.filter((item: any) => item.softNameAbbr === route.query.softNameAbbr)[0]

    // 初始化时设置语言模式
    if (isTraditional) {
      zh_tran('t')
    }

    // 文字转换方法，使用 lang.js 的 tran 方法
    function tran(text: string) {
      if (isTraditional) {
        return (text as any).tran()
      }
      return text
    }

    function switchLanguage() {
      if (isTraditional) {
        // 切换为繁体
        zh_tran('t')
      }
    }

    async function gettoken() {
      pageData.token = await globalsets.getToken()
      if (route.query.market && route.query.code) {
        pageData.radiovalue = 4
        // fetchTradeDays(route.query.market as string, route.query.code as string)
      } else {
        pageData.radiovalue = 1
      }

      if (route.query.TradeDay) {
        const t = route.query.TradeDay as string
        if (t.includes('-')) {
          pageData.selectedDate = window.moment(t)
        } else {
          const timestamp = Number(t)
          if (!isNaN(timestamp)) {
            pageData.selectedDate = window.moment(timestamp * 1000)
          }
        }
        // 初始化 lastValidDate
        if (pageData.selectedDate && !pageData.selectedDate.isAfter(window.moment(), 'day')) {
          pageData.lastValidDate = pageData.selectedDate.clone()
        }
      }

      // token 获取完成后调用 getlist（getlist 内部会处理 showTradeDay 参数）
      if (route.query.token) {
        getlist()
      }
    }
    gettoken()

    // 页面加载完成后切换语言
    onMounted(() => {
      nextTick(() => {
        switchLanguage()


      })
    })

    // 监听 loading 状态变化，数据加载完成后重新切换语言
    watch(() => pageData.loading, (newVal, oldVal) => {
      if (oldVal === true && newVal === false) {
        // 数据加载完成后，延迟执行确保 DOM 完全渲染
        nextTick(() => {
          setTimeout(() => {
            switchLanguage()
          }, 100)
        })
      }
    })

    // 监听笔记列表变化，重新切换语言
    watch(() => pageData.noteslist.length, () => {
      nextTick(() => {
        setTimeout(() => {
          switchLanguage()
        }, 50)
      })
    })

    // 监听添加股票弹窗显示，自动聚焦输入框
    watch(() => pageData.showSelect, (visible) => {
      if (visible) {
        nextTick(() => {
          setTimeout(() => {
            const searchInput = document.querySelector('.stock-search-popover .ant-input') as HTMLInputElement
            if (searchInput) {
              searchInput.focus()
            }
          }, 100)
        })
      }
    })

    function fetchTradeDays(exch: string, inst: string) {
      if (!exch || !inst) return
      const data: any = {
        data: {
          Uid: defaultUid,
          ProjectID: pageData.currentProject.ProjectID,
          Exch: exch,
          Inst: inst
        },
        headers: { token: pageData.token }
      }

      stocknotesApi.getMsgboardNoteTradeDays(data).then((res: any) => {
        const { Data } = res
        if (Data && Array.isArray(Data)) {
          // Convert timestamps to YYYY-MM-DD strings
          pageData.tradeDays = Data.map((t: number) => window.moment(t * 1000).format('YYYY-MM-DD'))
        } else {
          pageData.tradeDays = []
        }
      })
    }

    const holidayCache = reactive<Record<number, { holidays: string[], workdays: string[] }>>({})

    // 静态导入节假日数据（确保打包时包含）
    const holidayDataModules: Record<number, any> = {}
    try {
      // @ts-ignore
      holidayDataModules[2024] = require('chinese-holidays/data/2024.json')
      // @ts-ignore
      holidayDataModules[2025] = require('chinese-holidays/data/2025.json')
      // @ts-ignore
      holidayDataModules[2026] = require('chinese-holidays/data/2026.json')
    } catch (e) {
      console.warn('Failed to load chinese-holidays data:', e)
    }

    function parseHolidayData(data: any) {
      const holidays: string[] = []
      const workdays: string[] = []

      if (Array.isArray(data)) {
        data.forEach((item: any) => {
          if (item.range) {
            const start = item.range[0]
            const end = item.range[1] || start
            const current = window.moment(start)
            const finish = window.moment(end)

            while (current.isSameOrBefore(finish)) {
              const dateStr = current.format('YYYY-MM-DD')
              if (item.type === 'holiday') {
                holidays.push(dateStr)
              } else if (item.type === 'workingday') {
                workdays.push(dateStr)
              }
              current.add(1, 'days')
            }
          }
        })
      }

      return { holidays, workdays }
    }

    function getYearData(year: number) {
      if (holidayCache[year]) return holidayCache[year]

      let holidays: string[] = []
      let workdays: string[] = []

      // 使用静态导入的数据
      if (holidayDataModules[year]) {
        console.log(`使用静态导入的数据1`)
        const parsed = parseHolidayData(holidayDataModules[year])
        holidays = parsed.holidays
        workdays = parsed.workdays
      } else {
        // 尝试动态加载其他年份
        try {
          // @ts-ignore
          const data = require(`chinese-holidays/data/${year}.json`)
          const parsed = parseHolidayData(data)
          holidays = parsed.holidays
          workdays = parsed.workdays
          console.log(`使用静态导入的数据2`)
        } catch (e) {
          console.warn(`No holiday data for ${year}, using weekend-only mode`)
        }
      }

      holidayCache[year] = { holidays, workdays }
      console.log(`使用静态导入的数据3`)
      return holidayCache[year]
    }

    function handleDateDisabled(current: any) {
      if (!current) return false

      // 只禁用未来日期（今天之后的日期不可选）
      if (current.isAfter(window.moment(), 'day')) {
        return true
      }

      return false
    }

    /**
     * 处理月份/年份导航，返回目标月份中的有效日期
     * @param currentValue 当前选中的日期
     * @param delta 变化量（正数向前，负数向后）
     * @param unit 变化单位（'month' 或 'year'）
     * @returns 目标月份中的有效日期，如果目标月份完全在未来则返回 null
     */
    function handleMonthChange(currentValue: any, delta: number, unit: 'month' | 'year'): any {
      if (!currentValue) return null

      const today = window.moment()
      // 计算目标月份的第一天
      const targetMonth = currentValue.clone().add(delta, unit).startOf('month')
      // 计算目标月份的最后一天
      const targetMonthEnd = targetMonth.clone().endOf('month')

      // 情况1：目标月份完全在未来（目标月份的第一天在今天之后）
      if (targetMonth.isAfter(today, 'day')) {
        return null
      }

      // 情况2：目标月份包含今天（今天在目标月份内）
      if (today.isSameOrAfter(targetMonth, 'day') && today.isSameOrBefore(targetMonthEnd, 'day')) {
        return today.clone()
      }

      // 情况3：目标月份完全在过去
      // 尝试保持同一天，如果超出月份天数则使用月末
      const originalDay = currentValue.date()
      const targetDate = targetMonth.clone().date(Math.min(originalDay, targetMonthEnd.date()))
      return targetDate
    }

    /**
     * 月份导航的包装函数，处理UI反馈逻辑
     * @param currentValue 当前日历显示的日期
     * @param delta 变化量
     * @param unit 变化单位
     * @param onChange 日历的onChange回调
     */
    function handleMonthNavigation(currentValue: any, delta: number, unit: 'month' | 'year', onChange: (date: any) => void) {
      const newDate = handleMonthChange(currentValue, delta, unit)

      if (newDate === null) {
        // 目标月份完全在未来，显示警告
        message.warn({
          content: tran('未来日期不可选'),
          top: '20px',
          duration: 2,
          class: 'message-right'
        })
        // 不调用 onChange，保持当前选择
        return
      }

      // 有效日期，更新 lastValidDate 并调用 onChange
      pageData.lastValidDate = newDate.clone()
      onChange(newDate)
    }

    function isRestDay(current: any) {
      if (!current) return false
      const dateStr = current.format('YYYY-MM-DD')
      const year = current.year()
      const { holidays } = getYearData(year)

      // 节假日
      if (holidays.includes(dateStr)) {
        return true
      }

      // 周末
      const day = current.day()
      if (day === 0 || day === 6) {
        return true
      }

      return false
    }

    function handleTabChange(val: number) {
      // 在检查未保存更改之前，先保存草稿笔记的内容
      // 因为 checkUnsavedChange 可能会修改 pageData.content
      const draftInList = pageData.noteslist.find((item: any) => !item.ID)
      let savedDraftContent = ''
      let savedDraftTitle = ''
      if (draftInList) {
        // 如果当前选中的是草稿笔记，使用当前编辑器的内容
        // 否则使用草稿对象自身保存的内容
        const isCurrentDraft = pageData.currentNote && !pageData.currentNote.ID
        savedDraftContent = isCurrentDraft ? pageData.content : draftInList.Content
        savedDraftTitle = isCurrentDraft ? pageData.currentNote.Title : draftInList.Title
      }

      checkUnsavedChange(() => {
        // 切换标签时，检查列表中是否存在草稿笔记（未保存的新建笔记）
        if (draftInList) {
          // 保存草稿到共享状态（两个Tab都能看到）
          pageData.draftNoteTab4 = {
            ...draftInList,
            Title: savedDraftTitle,
            Content: savedDraftContent,
            _isDraft: true,
            _tabValue: pageData.radiovalue
          }
          // 同时保存到Tab1，这样全部笔记也能看到
          pageData.draftNoteTab1 = pageData.draftNoteTab4
        }

        pageData.loading = true
        pageData.radiovalue = val
        getlist()
      })
    }

    // 统一的编辑器状态重置函数
    function resetEditorState() {
      pageData.anchorNode = null
      pageData.selection = null
      pageData.selectionIndex = 0
      pageData.content = ''
      pageData.currentNote = { index: -1, Title: '', Content: '', ID: '', PubTime: 0 }
    }

    // 获取当前Tab对应的草稿存储key
    function getDraftKey(tabValue: number): 'draftNoteTab1' | 'draftNoteTab4' {
      return tabValue === 1 ? 'draftNoteTab1' : 'draftNoteTab4'
    }

    function initOriginalState() {
      pageData.originalTitle = (pageData.currentNote.Title || '').trim()
      pageData.originalContent = (pageData.content || '').trim()
      pageData.originalDate = pageData.selectedDate ? pageData.selectedDate.format('YYYY-MM-DD') : ''
    }

    // 标准化内容，去除格式差异
    function normalizeContent(content: string): string {
      if (!content) return ''
      // 1. 去除 &nbsp; 替换为普通空格
      let normalized = content.replace(/&nbsp;/g, ' ')
      // 2. 去除 HTML 标签之间的空白字符（换行、多余空格等）
      normalized = normalized.replace(/>\s+</g, '><')
      // 3. 去除标签内属性值周围可能的格式差异
      normalized = normalized.replace(/\s+/g, ' ')
      // 4. 去除首尾空白
      normalized = normalized.trim()
      // 5. 去除空的 div/span 标签
      normalized = normalized.replace(/<(div|span)[^>]*>\s*<\/\1>/gi, '')
      return normalized
    }

    function hasUnsavedChanges() {
      // 如果列表为空，说明没有数据，不需要检测
      if (pageData.noteslist.length === 0) return false

      // 如果没有当前笔记，不需要检测
      if (!pageData.currentNote) return false

      // 如果是草稿笔记（没有ID），不需要检测未保存更改
      // 因为草稿本身就是未保存的状态
      if (!pageData.currentNote.ID) return false

      const currentTitle = (pageData.currentNote.Title || '').trim()
      const currentContent = normalizeContent(pageData.content)
      const originalContent = normalizeContent(pageData.originalContent)
      const currentDate = pageData.selectedDate ? pageData.selectedDate.format('YYYY-MM-DD') : ''

      // 只有当标题或内容真正发生变化时才返回 true
      const titleChanged = currentTitle !== pageData.originalTitle
      const contentChanged = currentContent !== originalContent
      const dateChanged = currentDate !== pageData.originalDate

      // 调试日志
      if (titleChanged || contentChanged || dateChanged) {
        console.log('hasUnsavedChanges detected:', {
          titleChanged,
          contentChanged,
          dateChanged,
          currentTitle,
          originalTitle: pageData.originalTitle,
          currentContent,
          originalContent
        })
      }

      return titleChanged || contentChanged || dateChanged
    }

    function checkUnsavedChange(nextAction: () => void) {
      if (hasUnsavedChanges()) {
        Modal.confirm({
          content: tran('检测到未保存的更改，离开页面将丢失所有修改。确定要继续吗？'),
          okText: tran('离开'),
          cancelText: tran('取消'),
          onOk() {
            // 只有当前笔记是有ID的笔记时才恢复状态
            // 草稿笔记的状态应该保持不变
            if (pageData.currentNote && pageData.currentNote.ID) {
              pageData.currentNote.Title = pageData.originalTitle
              pageData.content = pageData.originalContent
              pageData.currentNote.Content = pageData.originalContent

              if (pageData.originalDate) {
                pageData.selectedDate = window.moment(pageData.originalDate)
              }
            }
            nextAction()
          }
        })
      } else {
        nextAction()
      }
    }

    async function getlist(targetId?: string | number, tradeDay?: number) {
      pageData.loading = true
      const data: any = {}
      console.log('route.query.showTradeDay', route.query.showTradeDay)
      // 构造请求参数
      // radiovalue=1 时始终用 1，radiovalue=4 时根据 showTradeDay 决定用 3 还是 4
      const obj: any = {
        // Tab: pageData.radiovalue === 1 ? 1 : (route.query.showTradeDay ? 3 : pageData.radiovalue),
        Tab: pageData.radiovalue,
        Uid: defaultUid,
        ProjectID: defaultProjectID,
        PageID: 1,
        PageSize: 1000
      }

      // 如果路由有 showTradeDay 参数，解析并设置 TradeDay
      if (route.query.showTradeDay) {
        const dateStr = route.query.showTradeDay as string
        if (dateStr.length === 8) {
          // 格式 YYYYMMDD，解析为时间戳
          const year = dateStr.substring(0, 4)
          const month = dateStr.substring(4, 6)
          const day = dateStr.substring(6, 8)
          const momentDate = window.moment(`${year}-${month}-${day}`)
          if (momentDate.isValid()) {
            obj.TradeDay = Number(momentDate.format('X'))
            // 同步更新选中日期
            pageData.selectedDate = momentDate
            if (!momentDate.isAfter(window.moment(), 'day')) {
              pageData.lastValidDate = momentDate.clone()
            }
          }
        }
      } else if (tradeDay) {
        // 使用传入的 tradeDay 参数
        obj.TradeDay = tradeDay
      }

      // 个股相关 (Tab=4 或 Tab=3) 时，添加 Stocks 参数
      if (pageData.radiovalue === 4 || route.query.showTradeDay) {
        obj.Stocks = [
          {
            Exch: route.query.market,
            Inst: route.query.code
          }
        ]
      }

      data.data = obj
      console.log('我要刷新接口, obj:', obj)
      data.headers = { token: pageData.token }

      return stocknotesApi.getMsgboardNoteList(data).then((res: any) => {
        let { Data } = res
        // 如果是繁体，转换数据
        if (isTraditional && Data) {
          Data = objTran('t', Data)
        }
        if (Data) {
          pageData.noteslist = Data.sort((a: any, b: any) => b.PubTime - a.PubTime)

          // 如果有暂存的草稿笔记，将其插入到列表最前面
          const currentDraftKey = getDraftKey(pageData.radiovalue)
          const currentDraft = pageData[currentDraftKey]
          if (currentDraft) {
            // 恢复当前Tab对应的草稿
            pageData.noteslist.unshift(currentDraft)
          }

          // 如果指定了目标ID，优先选中该目标
          if (targetId) {
            const exists = pageData.noteslist.find((item: any) => item.ID === targetId)
            if (exists) {
              getNoteDetail(exists.ID)
              return
            }
          }

          // 如果路由有 showTradeDay 参数，匹配列表中的笔记
          if (route.query.showTradeDay) {
            const dateStr = route.query.showTradeDay as string
            if (dateStr.length === 8) {
              const year = dateStr.substring(0, 4)
              const month = dateStr.substring(4, 6)
              const day = dateStr.substring(6, 8)
              const momentDate = window.moment(`${year}-${month}-${day}`)
              if (momentDate.isValid()) {
                const tradeDayTimestamp = Number(momentDate.format('X'))
                // 查找匹配 TradeDay 的笔记
                console.log('pageData.noteslist', pageData.noteslist)
                const matchedNote = pageData.noteslist.find((item: any) => item.TradeDay === tradeDayTimestamp)
                if (matchedNote) {
                  getNoteDetail(matchedNote.ID)
                  // 滚动到对应位置
                  nextTick(() => {
                    scrollToNote(matchedNote.ID)
                  })
                  return
                }
              }
            }
          }

          // 如果有草稿笔记在列表中，选中它
          const draftInList = pageData.noteslist.find((item: any) => !item.ID)
          if (draftInList) {
            pageData.currentNote = draftInList
            pageData.content = draftInList.Content
            // 初始化原始状态，避免误报未保存更改
            initOriginalState()
          } else if (pageData.isFirstLoad) {
            // 首次加载时，默认打开新建笔记
            pageData.isFirstLoad = false
            setTimeout(() => {
              addEmptyNote(true)
            }, 100)
          } else if (pageData.noteslist.length > 0) {
            setTimeout(() => {
              getNoteDetail(pageData.noteslist[0].ID)
            }, 100)
          } else {
            pageData.currentNote = { index: -1 }
            pageData.content = ''
          }
        } else {
          pageData.noteslist = []
          const currentDraftKey = getDraftKey(pageData.radiovalue)
          const currentDraft = pageData[currentDraftKey]
          if (currentDraft) {
            pageData.noteslist.unshift(currentDraft)
            pageData.currentNote = currentDraft
            pageData.content = currentDraft.Content
            // 初始化原始状态，避免误报未保存更改
            initOriginalState()
          } else if (pageData.isFirstLoad) {
            // 首次加载且列表为空时，也打开新建笔记
            pageData.isFirstLoad = false
            setTimeout(() => {
              addEmptyNote(true)
            }, 100)
          } else {
            pageData.currentNote = { index: -1 }
            pageData.content = ''
          }
        }
      }).finally(() => {
        pageData.loading = false
        // 首次加载完成后触发渐入动画
        if (!pageData.pageLoaded) {
          nextTick(() => {
            pageData.pageLoaded = true
          })
        }
      })
    }

    // 滚动到指定笔记位置
    function scrollToNote(noteId: string | number) {
      nextTick(() => {
        const listContent = document.querySelector('.list-content ul')
        const noteIndex = pageData.noteslist.findIndex((item: any) => item.ID === noteId)
        if (listContent && noteIndex >= 0) {
          const noteItems = listContent.querySelectorAll('li')
          if (noteItems[noteIndex]) {
            noteItems[noteIndex].scrollIntoView({ behavior: 'smooth', block: 'center' })
          }
        }
      })
    }

    function getNoteDetail(id: number | string | undefined) {
      if (!id) {
        pageData.currentNote = { index: 0 }
        pageData.content = ''
        return
      }
      const data: any = {}
      data.data = { ID: id }
      data.headers = { token: pageData.token }

      stocknotesApi.getMsgboardNote(data).then((res: any) => {
        let { Data } = res
        // 如果是繁体，转换数据
        if (isTraditional && Data) {
          Data = objTran('t', Data)
        }
        pageData.currentNote = Data || {}
        // 优先使用 TradeDay，如果没有则使用 PubTime
        if (pageData.currentNote.TradeDay) {
          pageData.selectedDate = window.moment(pageData.currentNote.TradeDay * 1000)
          if (!pageData.selectedDate.isAfter(window.moment(), 'day')) {
            pageData.lastValidDate = pageData.selectedDate.clone()
          }
        } else if (pageData.currentNote.PubTime) {
          pageData.selectedDate = window.moment(pageData.currentNote.PubTime * 1000)
          if (!pageData.selectedDate.isAfter(window.moment(), 'day')) {
            pageData.lastValidDate = pageData.selectedDate.clone()
          }
        }
        pageData.dateChanged = false
        pageData.currentNote.index = pageData.noteslist.findIndex((item: any) => item.ID === pageData.currentNote.ID)
        pageData.content = handlecontent(Data.Content || '')
        initOriginalState()
      })
    }

    function addEmptyNote(skipUnsavedCheck: boolean | MouseEvent = false) {
      // 如果是 MouseEvent（从模板点击触发），转换为 false
      const shouldSkip = typeof skipUnsavedCheck === 'boolean' ? skipUnsavedCheck : false
      const doAddNote = () => {
        if (!route.query.token) {
          stock.gotoClientLogin()
          return
        }

        // 检查是否存在未保存的笔记（ID为空）
        const unsavedNote = pageData.noteslist.find((item: any) => !item.ID)
        if (unsavedNote) {
          message.warning(tran('请完善当前笔记'))
          // 选中已存在的草稿笔记，并聚焦标题输入框
          pageData.currentNote = unsavedNote
          pageData.content = unsavedNote.Content || ''
          nextTick(() => {
            setTimeout(() => {
              const titleInput = document.querySelector('.title-input') as HTMLInputElement
              if (titleInput) {
                titleInput.focus()
              }
            }, 50)
          })
          return
        }

        // 先重置编辑器状态，确保状态干净
        resetEditorState()

        const data: any = {
          Uid: defaultUid,
          ProjectID: defaultProjectID,
          ProjectName: defaultProjectName,
          Exch: route.query.market,
          Inst: route.query.code,
          Title: '',
          Content: '',
          TradeDay: today,
          PubTime: today,
          ID: '',
          index: 0
        }

        pageData.currentNote = data
        pageData.content = ''

        if (data.PubTime) {
          pageData.selectedDate = window.moment(data.PubTime * 1000)
          if (!pageData.selectedDate.isAfter(window.moment(), 'day')) {
            pageData.lastValidDate = pageData.selectedDate.clone()
          }
        }
        pageData.dateChanged = false
        pageData.noteslist.unshift(data)

        // 如果有股票代码和名称，直接从路由参数拼接插入（不调接口）
        if (route.query.code && route.query.market && route.query.name) {
          const code = route.query.code as string
          const market = route.query.market as string
          const name = decodeURIComponent(route.query.name as string)
          const tag = `$-${name}(${code}.${market})-$`

          // 先转换成HTML格式，保持与编辑器输出一致
          const htmlContent = handlecontent(tag)
          pageData.content = htmlContent
          pageData.currentNote.Content = htmlContent
          // 同步更新列表中的草稿对象
          data.Content = htmlContent
        }

        // 在所有内容设置完成后，初始化原始状态
        initOriginalState()

        // 新建笔记后，将光标焦点定位到标题输入框（使用延时确保DOM更新完成）
        nextTick(() => {
          setTimeout(() => {
            const titleInput = document.querySelector('.title-input') as HTMLInputElement
            if (titleInput) {
              titleInput.focus()
            }
          }, 50)
        })
      }

      // 首次加载时跳过未保存检查
      if (shouldSkip) {
        doAddNote()
      } else {
        checkUnsavedChange(doAddNote)
      }
    }



    function getCurrentNote(item: note) {
      if (pageData.currentNote === item) return;
      if (pageData.currentNote.ID === item.ID && item.ID) return;

      const performSwitch = () => {
        // 如果离开的是新建笔记（没有ID），检查是否需要从列表中移除
        if (pageData.currentNote && !pageData.currentNote.ID) {
          const hasContent = (pageData.currentNote.Title && pageData.currentNote.Title.trim()) ||
            (pageData.content && (stripHtml(pageData.content).trim() || pageData.content.includes('<img')));

          if (!hasContent) {
            // 没有内容，从列表中移除这个空的新建笔记
            const emptyNoteIndex = pageData.noteslist.findIndex((n: any) => !n.ID)
            if (emptyNoteIndex > -1) {
              pageData.noteslist.splice(emptyNoteIndex, 1)
            }
          }

        }

        if (!item.ID) {
          // 切换到草稿笔记时，先重置编辑器状态
          pageData.anchorNode = null
          pageData.selection = null
          pageData.selectionIndex = 0

          pageData.currentNote = item
          pageData.content = item.Content || ''

          if (item.PubTime) {
            pageData.selectedDate = window.moment(item.PubTime * 1000)
            if (!pageData.selectedDate.isAfter(window.moment(), 'day')) {
              pageData.lastValidDate = pageData.selectedDate.clone()
            }
          }
          pageData.dateChanged = false

          // 如果内容为空且有股票代码，自动查询并插入股票信息

          initOriginalState()

          // 切换到草稿笔记后，聚焦标题输入框
          nextTick(() => {
            setTimeout(() => {
              const titleInput = document.querySelector('.title-input') as HTMLInputElement
              if (titleInput) {
                titleInput.focus()
              }
            }, 50)
          })
        } else {
          getNoteDetail(item.ID)
          if (item.Exch && item.Inst) {
            //  fetchTradeDays(item.Exch, item.Inst)
          }
        }
      }

      checkUnsavedChange(performSwitch)
    }

    function handleSearch(val: any) {
      if (!val) {
        pageData.options = []
        return
      }
      const data: any = {}
      data.data = {
        // 市场类别：沪深股 + 港股 + 美股 + 期货
        // 为空代表全部，这里显式限制到四大类
        markettype: ['CN', 'HK', 'US', 'FU'],
        // 具体市场：沪深（含ETF）、北交所、港股、美股、期货/指数
        marketcode: 'HKSE,NASDAQ,NYSE,AMEX,HKINDEX,USINDEX,shetf,szetf,sh,sz,nsb,USSECTOR,HKSECTOR,HKEX,AHZSECTOR,COMEX,HKQZ,SGX,FHSI,HKIDX,NASIDX,CME',
        key: val,          // 接口本身支持名称/代码/拼音首字母

        active: true
      }
      data.headers = { 'Content-Type': 'application/x-www-form-urlencoded' }

      allApi.searchStock(data).then((res: any) => {
        let list = res.Data || []
        const priority: Record<string, number> = {
          // 港股优先
          HKSE: 1,
          HKINDEX: 1,
          // 沪深北 + ETF
          sh: 2,
          sz: 2,
          bj: 2,
          shetf: 2,
          szetf: 2,
          // 美股
          NASDAQ: 3,
          NYSE: 3,
          AMEX: 3,
        }

        list.sort((a: any, b: any) => {
          const pA = priority[a.MarketCode] || 99
          const pB = priority[b.MarketCode] || 99
          return pA - pB
        })

        pageData.options = list
      })
    }

    function handleChange(val: any) {
      const tag = `$-${val.Name}(${val.Code}.${val.MarketCode})-$`
      insertAtCursor(tag)
      pageData.showSelect = false
      pageData.searchText = ''
      pageData.options = []
    }

    function handleDateChange(date: any, dateString: string) {
      if (dateString) {
        const tag = `$-${dateString}-$`
        insertAtCursor(tag)
      }
    }

    function onDateSelect(date: any) {
      if (date) {
        const dateString = date.format('YYYY-MM-DD')
        // Verify if disabled? a-calendar should prevent selecting disabled dates but let's be safe
        if (handleDateDisabled(date)) {
          return
        }
        pageData.selectedDate = date
        pageData.dateChanged = true
        // const tag = `$-${dateString}-$`
        // insertAtCursor(tag)
        pageData.showDateSelect = false
      }
    }

    function insertAtCursor(text: string) {
      const editorElem = document.querySelector('.editwrap div') // Divedit root

      if (!editorElem) {
        pageData.content += text
        nextTick(() => {
          pageData.content = handlecontent(pageData.content)
        })
        return
      }

      // 优先使用保存的 Range 对象
      if (savedRange && editorElem.contains(savedRange.commonAncestorContainer)) {
        try {
          // 创建一个临时的文本标记
          const marker = `__INSERT_MARKER_${Date.now()}__`

          // 在 Range 位置插入标记
          savedRange.deleteContents()
          const markerNode = document.createTextNode(marker)
          savedRange.insertNode(markerNode)

          // 获取带标记的 HTML
          const htmlWithMarker = editorElem.innerHTML || ''

          // 用股票标签替换标记
          const newHtml = htmlWithMarker.replace(marker, text)

          // 处理内容并更新
          pageData.content = handlecontent(newHtml)
          if (pageData.currentNote) {
            pageData.currentNote.Content = pageData.content
          }

          // 清空保存的 Range
          savedRange = null
          return
        } catch (e) {
          console.warn('使用保存的 Range 插入失败，尝试其他方式', e)
        }
      }

      // Fallback: 追加到末尾
      pageData.content += text
      nextTick(() => {
        pageData.content = handlecontent(pageData.content)
      })
    }

    function escapeRegExp(string: string) {
      return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    }

    // 构建表情正则（只构建一次）
    const allEmojis = [...emojiArray, ...emojiArrayV2];
    allEmojis.sort((a, b) => b.length - a.length); // 长代码优先
    const emojiPattern = allEmojis.map(escapeRegExp).join('|');
    const customEmojiReg = new RegExp(emojiPattern, 'g');

    function handlecontent(content: string) {
      let str = content || ''

      // 先保护已有的 contentstock span 标签，避免被重复处理
      const protectedSpans: string[] = []
      str = str.replace(/<span class="contentstock[^"]*"[^>]*>[^<]*<\/span>/g, (match) => {
        const index = protectedSpans.length
        protectedSpans.push(match)
        return `__PROTECTED_SPAN_${index}__`
      })

      // 1. 股票代码替换（保持字符串替换，因为它生成特定 HTML 结构）
      // 判断是否需要禁用沪深股票 - 只处理原始格式 $-...-$
      str = str.replace(/\$-([^\(]+)\(([^.]+)\.([^)]+)\)-\$/g, (match, name, code, market) => {
        const marketLower = (market || '').toLowerCase()
        const isHuShen = marketLower === 'sh' || marketLower === 'sz'
        const disabled = isFangDeZhiTou.value && isHuShen
        const className = disabled ? 'contentstock disabled' : 'contentstock'
        return `<span class="${className}" contenteditable=false>${name}(${code}.${market})</span>`
      })
      str = str.replace(/\$-(\d{4}-\d{2}-\d{2})-\$/g, '<span class="contentstock" contenteditable=false>$1</span>')
      str = str.replace(/\$-/g, '<span class="contentstock" contenteditable=false>$')
      str = str.replace(/-\$/g, '$</span> ')

      // 恢复被保护的 span 标签
      str = str.replace(/__PROTECTED_SPAN_(\d+)__/g, (match, index) => {
        return protectedSpans[parseInt(index)] || match
      })

      // 2. 表情替换 - 使用 DOM 解析防止破坏现有 HTML 结构（特别是 img 标签的属性）
      try {
        const tempDiv = document.createElement('div')
        tempDiv.innerHTML = str

        walkAndReplace(tempDiv)

        return tempDiv.innerHTML
      } catch (e) {
        console.error('Emoji parse error:', e)
        return str
      }
    }

    function walkAndReplace(node: Node) {
      if (node.nodeType === 3) { // Node.TEXT_NODE
        const text = node.nodeValue || '';
        // 尝试替换表情
        const replacedHtml = text.replace(customEmojiReg, (r) => {
          if (emojiArray.indexOf(r) > -1) {
            const index = emojiArray.indexOf(r)
            const src = getEmojiUrl(index)
            return `<img class="emojiMsg" code="${r}" src="${src}" oncontextmenu="return false"/>`
          } else if (emojiArrayV2.indexOf(r) > -1) {
            const index = emojiArrayV2.indexOf(r)
            const src = getEmojiV2Url(index)
            return `<img class="emojiMsg" code="${r}" src="${src}" oncontextmenu="return false"/>`
          }
          return r
        });

        // 如果文本发生了变化（说明包含表情代码）
        if (replacedHtml !== text) {
          const span = document.createElement('span');
          span.innerHTML = replacedHtml;
          const parent = node.parentNode;
          if (parent) {
            // 将生成的节点插入到原文本节点前
            while (span.firstChild) {
              parent.insertBefore(span.firstChild, node);
            }
            // 移除原文本节点
            parent.removeChild(node);
          }
        }
      } else if (node.nodeType === 1) { // Node.ELEMENT_NODE
        const el = node as Element;
        // 跳过脚本、样式表和已生成的表情图片
        if (el.tagName === 'SCRIPT' || el.tagName === 'STYLE' || el.classList.contains('emojiMsg')) {
          return;
        }
        // 递归遍历子节点（使用 Array.from 创建副本，因为遍历过程中可能会修改 DOM 结构）
        Array.from(node.childNodes).forEach(child => walkAndReplace(child));
      }
    }

    function changecontent(content: string) {
      if (typeof content === 'string') {
        pageData.content = content
        pageData.selectionIndex = pageData.content.length
        if (pageData.currentNote) {
          pageData.currentNote.Content = content
          // 如果当前是草稿笔记，同步更新列表中的草稿对象
          if (!pageData.currentNote.ID) {
            const draftInList = pageData.noteslist.find((item: any) => !item.ID)
            if (draftInList) {
              draftInList.Content = content
            }
          }
        }
      }
    }

    function markEdited() {
      // 如果当前是草稿笔记，同步更新列表中的草稿对象的标题
      if (pageData.currentNote && !pageData.currentNote.ID) {
        const draftInList = pageData.noteslist.find((item: any) => !item.ID)
        if (draftInList && draftInList !== pageData.currentNote) {
          draftInList.Title = pageData.currentNote.Title
        }
      }
    }

    function submitNote() {
      if (!pageData.currentNote.Title || !pageData.currentNote.Title.trim()) {
        message.error(tran('请输入标题'))
        return
      }

      // 优先从编辑器 DOM 获取最新内容，避免 pageData.content 未同步的问题
      let contentToValidate = pageData.content
      const editorElem = document.querySelector('.editwrap div[contenteditable]') as HTMLElement
      if (editorElem) {
        contentToValidate = editorElem.innerHTML || ''
        // 同步更新 pageData.content，确保后续保存使用最新内容
        if (contentToValidate !== pageData.content) {
          pageData.content = contentToValidate
          if (pageData.currentNote) {
            pageData.currentNote.Content = contentToValidate
          }
        }
      }

      const plainText = stripHtml(contentToValidate).trim()
      if (!plainText) {
        message.error(tran('请输入内容'))
        return
      }


      if (pageData.currentNote.ID) {
        updateNote()
      } else {
        addNote()
      }
    }

    function restoreContent(content: string) {
      if (!content) return ''
      try {
        const tempDiv = document.createElement('div')
        tempDiv.innerHTML = content

        // 1. 还原股票标签：<span class="contentstock">name(code.market)</span> -> $-name(code.market)-$
        const stocks = tempDiv.querySelectorAll('.contentstock')
        stocks.forEach(span => {
          const text = span.textContent || ''
          // 还原为 $-...-$ 格式
          const textNode = document.createTextNode(`$-${text}-$`)
          span.parentNode?.replaceChild(textNode, span)
        })

        // 2. 还原表情图片
        const imgs = tempDiv.querySelectorAll('img.emojiMsg')
        imgs.forEach(img => {
          const code = img.getAttribute('code')
          if (code) {
            const textNode = document.createTextNode(code)
            img.parentNode?.replaceChild(textNode, img)
          }
        })
        return tempDiv.innerHTML
      } catch (e) {
        return content
      }
    }

    // 从编辑内容中提取所有股票信息
    function extractStocksFromContent(content: string): Array<{ Exch: string; Inst: string }> {
      const stocks: Array<{ Exch: string; Inst: string }> = []
      // 先将 HTML 内容还原为原始格式
      const restoredContent = restoreContent(content)
      // 匹配 $-名称(代码.市场)-$ 格式
      // 先匹配整个 $-...-$ 块，然后解析最后一个括号中的代码和市场
      const blockRegex = /\$-(.+?)-\$/g
      let blockMatch
      while ((blockMatch = blockRegex.exec(restoredContent)) !== null) {
        const inner = blockMatch[1]
        // 找到最后一个 (xxx.xxx) 格式的括号
        const lastParenMatch = inner.match(/^.*\(([^.()]+)\.([^()]+)\)$/)
        if (lastParenMatch) {
          const inst = lastParenMatch[1] // 代码
          const exch = lastParenMatch[2] // 市场
          // 避免重复添加
          const exists = stocks.some(s => s.Exch === exch && s.Inst === inst)
          if (!exists) {
            stocks.push({ Exch: exch, Inst: inst })
          }
        }
      }
      return stocks
    }

    function updateNote() {
      const data: any = {}
      const contentToSave = restoreContent(pageData.content)
      // 从编辑内容中提取股票列表
      const stocks = extractStocksFromContent(pageData.content)

      data.data = {
        Uid: defaultUid,
        ProjectID: defaultProjectID,
        ProjectName: defaultProjectName,
        Title: pageData.currentNote.Title,
        Content: contentToSave,
        ID: pageData.currentNote.ID,
        TradeDay: pageData.dateChanged && pageData.selectedDate ? Number(pageData.selectedDate.format('X')) : (pageData.currentNote.TradeDay || today),
        Stocks: stocks
        // PubTime: Date.now() / 1000
      }
      data.headers = { token: pageData.token }

      stocknotesApi.getMsgboardUpdateNote(data).then((res: any) => {
        const { Code, Data } = res
        if (Code === 0) {
          // 保存成功前，先检查并保存列表中的草稿笔记
          const draftInList = pageData.noteslist.find((item: any) => !item.ID)
          if (draftInList) {
            // 保存草稿到状态中，这样 getlist 后可以恢复
            pageData.draftNoteTab4 = {
              ...draftInList,
              _isDraft: true,
              _tabValue: pageData.radiovalue
            }
            pageData.draftNoteTab1 = pageData.draftNoteTab4
          }

          // 保存成功后，重新获取列表并保持选中当前笔记
          getlist(pageData.currentNote.ID)
          message.success('保存成功')
          initOriginalState() // 保存成功后更新原始状态

          // 保存成功后，调用客户端指令刷新外部的 StockNote
          const url = `gw://funccall?funcname=OnFireCommand;Command=RefreshStockNote`
          window.open(url)
          console.log('调用客户端指令刷新外部的 保存成功')
        } else if (Code === 3) {
          message.error(tran('您的笔记过长，不能保存；请检查：标题不超过50个字，内容不超过1500个字！'))
        }
      })
    }

    function addNote() {
      const data: any = {}
      const contentToSave = restoreContent(pageData.content)
      // 从编辑内容中提取股票列表
      const stocks = extractStocksFromContent(pageData.content)

      data.data = {
        Uid: defaultUid,
        ProjectID: defaultProjectID,
        ProjectName: defaultProjectName,
        Stocks: stocks,
        Title: pageData.currentNote.Title,
        Content: contentToSave,
        TradeDay: pageData.selectedDate ? Number(pageData.selectedDate.format('X')) : today
      }
      data.headers = { token: pageData.token }

      stocknotesApi.getMsgboardCreateNote(data).then((res: any) => {
        const { Code, Data } = res
        if (Code === 0) {
          // 新建成功后，清空草稿状态
          pageData.draftNoteTab1 = null
          pageData.draftNoteTab4 = null

          // 标记不再是首次加载，防止 getlist 后再次触发 addEmptyNote
          pageData.isFirstLoad = false

          // 重置编辑器状态，防止残留内容
          resetEditorState()

          // 获取新创建笔记的ID，刷新列表后选中它
          const newNoteId = Data?.ID
          getlist(newNoteId)
          message.success('新建成功')

          // 新建成功后，调用客户端指令刷新外部的 StockNote
          if (pageData.currentProject) {
            const url = `gw://funccall?funcname=OnFireCommand;Command=RefreshStockNote`
            window.open(url)
            console.log('调用客户端指令刷新外部的 新建成功')
          }
        } else if (Code === 3) {
          message.error(tran('您的笔记过长，不能保存；请检查：标题不超过50个字，内容不超过1500个字！'))
        }
      })
    }

    function onRightClick(item: any, e: any) {
    }

    function deleteNoteById(item: any) {
      Modal.confirm({
        content: '删除该笔记后不可恢复！',
        okText: '删除',
        onOk() {
          if (!item.ID) {
            // 本地删除未保存的笔记（草稿）
            const index = pageData.noteslist.indexOf(item)
            if (index > -1) {
              pageData.noteslist.splice(index, 1)

              // 清理两个Tab的草稿状态（因为草稿是共享的）
              pageData.draftNoteTab1 = null
              pageData.draftNoteTab4 = null

              // 重置编辑器状态
              resetEditorState()

              // 删除后选中第一个笔记（如果有的话）
              if (pageData.noteslist.length > 0) {
                const firstNote = pageData.noteslist[0]
                if (firstNote.ID) {
                  getNoteDetail(firstNote.ID)
                } else {
                  pageData.currentNote = firstNote
                  pageData.content = firstNote.Content || ''
                }
              }

              // 重置原始状态，避免切换笔记时误报有未保存更改
              initOriginalState()

              message.success('删除成功')
            }
          } else {
            performDelete(item.ID)
          }
        }
      })
    }

    function performDelete(id: string) {
      const data: any = {}
      data.data = {
        ID: id,
        Uid: route.query.uid,
        ProjectID: route.query.ProjectID
      }
      data.headers = { token: pageData.token }

      stocknotesApi.getMsgboardDeleteNote(data).then((res: any) => {
        if (res.Code === 0) {
          message.success('删除成功')
          // 从本地列表中移除已删除的笔记
          const deleteIndex = pageData.noteslist.findIndex((item: any) => item.ID === id)
          if (deleteIndex > -1) {
            pageData.noteslist.splice(deleteIndex, 1)
          }

          // 检查列表中是否有草稿笔记
          const draftNote = pageData.noteslist.find((item: any) => !item.ID)

          if (draftNote) {
            // 如果有草稿笔记，选中它
            pageData.currentNote = draftNote
            pageData.content = draftNote.Content || ''
          } else if (pageData.noteslist.length > 0) {
            // 如果没有草稿但有其他笔记，选中第一个
            const firstNote = pageData.noteslist[0]
            getNoteDetail(firstNote.ID)
          } else {
            // 列表为空，重置编辑器状态
            resetEditorState()
          }

          // 重置原始状态
          initOriginalState()

          // 删除成功后，调用客户端指令刷新外部的 StockNote
          const url = `gw://funccall?funcname=OnFireCommand;Command=RefreshStockNote`
          window.open(url)
          console.log('调用客户端指令刷新外部的 删除成功后，调用客户端指令刷新外部的')
        }
      })
    }

    function execBold() {
      document.execCommand('bold')
    }

    function getGb() {
      const selection: any = getSelection()
      pageData.selection = JSON.parse(JSON.stringify(selection))
      pageData.anchorNode = selection.anchorNode
      pageData.selectionIndex = selection.baseOffset
    }

    // 在打开添加股票弹窗前保存光标位置
    function saveSelectionBeforePopover() {
      const selection = window.getSelection()
      if (selection && selection.rangeCount > 0) {
        const editorElem = document.querySelector('.editwrap div')
        // 只有当光标在编辑器内时才保存
        if (editorElem && selection.anchorNode && editorElem.contains(selection.anchorNode)) {
          // 克隆 Range 对象，这样即使选区改变也能保留位置
          savedRange = selection.getRangeAt(0).cloneRange()
          pageData.anchorNode = selection.anchorNode
          pageData.selectionIndex = selection.anchorOffset
        }
      }
    }

    function stripHtml(html: string) {
      const tmp = document.createElement('div');
      tmp.innerHTML = html || '';
      const txt = tmp.textContent || tmp.innerText || '';
      return txt.length > 20 ? txt.slice(0, 20) + '...' : txt;
    }

    function getMarketName(code: string) {
      const map: any = {
        // 沪深北
        sh: '沪A',
        sz: '深A',
        bj: '北A',
        // 港股相关
        HKSE: '港股',
        HKINDEX: '港股',
        HKIDX: '港股',
        HKSECTOR: '港股',
        HKEX: '港股',
        HKQZ: '港股',
        hk: '港股',
        // 美股相关
        NASDAQ: '美股',
        NYSE: '美股',
        AMEX: '美股',
        USINDEX: '美股',
        USSECTOR: '美股',
        NASIDX: '美股',
        us: '美股',
        // 基金（ETF + 场外）
        shetf: '基金',
        szetf: '基金',
        AHZSECTOR: 'A股板块',
        // 期货 / 衍生品
        COMEX: '期货',
        SGX: '期货',
        FHSI: '期货',
        CME: '期货',
        // 其他
        nsb: '新三板',
      }
      return map[code] || code
    }

    function getEmojiUrl(index: number) {
      return `https://upload.techgp.cn/production/im/emoji/${index}.png`
    }

    function getEmojiV2Url(index: number) {
      let name
      if (index < 9) {
        name = 'ic_emoji_0' + (index + 1)
      } else {
        name = 'ic_emoji_' + (index + 1)
      }
      return `https://upload.techgp.cn/production/static/article/${name}.png`
    }

    function handleEmojiSelect(item: any, type: 'basic' | 'v2', index: number) {
      let imgTag = ''
      if (type === 'basic') {
        const code = emojiArray[index]
        const src = getEmojiUrl(index)
        imgTag = `<img class="emojiMsg" code="${code}" src="${src}" oncontextmenu="return false" style="width:24px;height:24px;vertical-align:middle;margin:0 2px;"/>`
      } else {
        const code = emojiArrayV2[index]
        const src = getEmojiV2Url(index)
        imgTag = `<img class="emojiMsg" code="${code}" src="${src}" oncontextmenu="return false" style="width:24px;height:24px;vertical-align:middle;margin:0 2px;"/>`
      }
      insertAtCursor(imgTag)
      pageData.showEmojiSelect = false
    }

    watch(
      () => route.query.theme,
      (val: any) => {
        pageData.theme = val as string
      }
    )

    watch(() => pageData.selectedDate, (newVal, oldVal) => {
      if (newVal) {
        // 如果选中了未来时间
        if (newVal.isAfter(window.moment(), 'day')) {
          message.warn({
            content: tran('未来日期不可选'),
            top: '20px',
            duration: 2,
            class: 'message-right'
          })
          nextTick(() => {
            if (pageData.lastValidDate) {
              pageData.selectedDate = pageData.lastValidDate.clone()
            } else {
              pageData.selectedDate = window.moment()
            }
          })
        } else {
          // 有效日期，更新记录
          // 注意：这里我们只记录非未来日期为有效，不考虑是否是节假日（因为用户只提到了未来时间）
          // 如果需要严格限制节假日，也可以在这里加判断
          pageData.lastValidDate = newVal.clone()
        }
      }
    })

    return {
      ...toRefs(pageData),
      tran,
      addEmptyNote,
      handleSearch,
      handleChange,
      handleDateChange,
      handleTabChange,
      changecontent,
      submitNote,
      getGb,
      getCurrentNote,
      deleteNoteById,
      onRightClick,
      execBold,
      moment: window.moment,
      getMarketName,
      stripHtml,
      diveditRef,
      markEdited,
      handleDateDisabled,
      onDateSelect,
      isRestDay,
      emojis,
      emojiArrayV2,
      getEmojiUrl,
      getEmojiV2Url,
      handleEmojiSelect,
      isFangDeZhiTou,
      saveSelectionBeforePopover,
      handleMonthNavigation
    }
  }
})
